import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { grantsService } from '../services/api';
import GrantForm from './GrantForm';
import './Grants.css';
import { useAuth } from '../contexts/AuthContext';
import { formatPropertyName } from '../utils/strings';

const Grants = () => {
  const navigate = useNavigate();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;
  const searchTimeoutRef = useRef(null);
  const { isAdmin } = useAuth();

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const fetchGrants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        page,
        limit: itemsPerPage,
      };

      if (debouncedSearchTerm) {
        filters.search = debouncedSearchTerm;
      }

      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const response = await grantsService.list(filters);
      setGrants(response.data.data || []);
      setTotal(response.data.pagination.total || 0);
      setTotalPages(response.data.pagination.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch grants');
      console.error('Error fetching grants:', err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchTerm, statusFilter, itemsPerPage]);

  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  const handleDelete = async (id) => {
    if (window.confirm('Delete this grant?')) {
      try {
        await grantsService.delete(id);
        setGrants(grants.filter(g => g.id !== id));
        alert('Grant deleted successfully');
      } catch (err) {
        alert('Failed to delete grant: ' + err.message);
      }
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setPage(1);
    fetchGrants();
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'applied':
        return 'badge-warning';
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      case 'in_progress':
        return 'badge-info';
      case 'funded':
        return 'badge-success';
      default:
        return 'badge-default';
    }
  };

  if (error && !loading) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div className="grants-container">
      <div className="page-header">
        <h1>
          Grants
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            + Grant
          </button>
        </h1>
      </div>

      {showForm && (
        <GrantForm 
          onSubmit={handleFormSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search grants by name or organization..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
            disabled={loading}
          />
        </div>

        <div className="filter-group">
          <select 
            value={statusFilter}
            onChange={handleStatusFilter}
            className="filter-select"
            disabled={loading}
          >
            <option value="all">All Statuses</option>
            <option value="applied">Applied</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="in_progress">In Progress</option>
            <option value="funded">Funded</option>
          </select>
        </div>
      </div>

      <div className="results-summary">
        Showing {(page - 1) * itemsPerPage + 1} to {Math.min(page * itemsPerPage, total)} of {total} grants
      </div>

      {loading && (
        <div className="loading">Loading grants...</div>
      )}

      {!loading && grants.length === 0 ? (
        <div className="empty-state">
          <h3>No grants found</h3>
          <p>Try adjusting your search or filters</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            Create First Grant
          </button>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="grants-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Organization</th>
                  <th>Amount Requested</th>
                  <th>Applied Date</th>
                  <th>Status</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {grants.map(grant => (
                  <tr 
                    key={grant.id}
                    className="table-row"
                    onClick={() => navigate(`/grants/${grant.id}`)}
                  >
                    <td className="name-cell">{grant.name}</td>
                    <td>{grant.organization_name || '-'}</td>
                    <td>
                      {grant.is_in_kind ? (
                        <span className="in-kind-label">In-Kind</span>
                      ) : grant.amount_requested ? (
                        `$${Number(grant.amount_requested).toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}`
                      ) : '-'}
                    </td>
                    <td>
                      {grant.date_applied ? new Date(grant.date_applied).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(grant.status)}`}>
                        {formatPropertyName(grant.status)}
                      </span>
                    </td>
                    {isAdmin && <td className="actions-cell">
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(grant.id);
                        }}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-outline"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loading}
              >
                ← Previous
              </button>

              <div className="page-info">
                Page {page} of {totalPages}
              </div>

              <button 
                className="btn btn-outline"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || loading}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Grants;
